'use client'

import { Button } from '@repo/design-system-cf/components/button'
import { tw } from 'utils/classnames'
import { CheckIcon } from 'primitives/icons'
import type { SiteDataQuery } from 'codegen/generated/cf'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { StartSubscriptionButton } from 'payments/start-subscription-button'
import {
  PAYMENT_ENTRY_POINTS,
  savePaymentModalEntryPoint,
} from 'payments/payments-dialog/analytics'
import { AppRoutes } from '@repo/web-utils/app-routes'

import { BannerContainer } from './container'
import { SubscriptionPlan } from '@repo/graphql-types'

interface Props {
  banner: Omit<
    NonNullable<SiteDataQuery['siteData']['allAccessYearlyBanner']>,
    '__typename' | 'type'
  > & {
    type: string
    underButtonText: string
  }
}

export function CoinsSubscription({ banner }: Props) {
  const { features, headline, buttonText, underButtonText } = banner

  function onButtonClick() {
    const isDailyGiftsPage = window.location.pathname.startsWith(AppRoutes.DAILY_GIFTS)
    savePaymentModalEntryPoint(
      isDailyGiftsPage
        ? PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV_DAILY_GIFTS
        : PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV,
    )

    sendAmplitudeEvent(AmplitudeEvents.CLICKED_BANNER_ABOVE_NAV, {
      banner_above_nav: banner.bannerAboveNav,
      banner_above_nav_state: banner.bannerAboveNavState,
    })
  }

  const backgroundImage: string | null = banner.backgroundImage

  return (
    <div
      style={{
        '--checkIconColor': '#3ab561',
      }}
    >
      <BannerContainer
        backgroundImage={backgroundImage}
        wrapperClassName="items-start"
        bannerType={banner.type}
      >
        <div className="w-full">
          <div
            className="mb-[10px] text-center text-[12px] leading-7 tracking-[0.3px] text-[#2A1F3F] md:text-left md:text-[16px] [&_.u-bold]:text-[16px] [&_.u-bold]:font-bold md:[&_.u-bold]:text-[24px]"
            dangerouslySetInnerHTML={{ __html: headline }}
          />

          <Button
            asChild
            onClick={onButtonClick}
            variant="primary"
            size="large"
            className="w-full uppercase bg-secondary-700 hover:bg-[#1b6633] md:hidden"
          >
            <StartSubscriptionButton
              subscriptionPlan={SubscriptionPlan.CoinPackMonthly700k}
              isTrial={false}
              entryPoint={PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV}
            >
              <span>{buttonText}</span>
            </StartSubscriptionButton>
          </Button>

          {features ? (
            <ul className="mt-3.5 hidden md:block">
              {features.map((feat, index) => {
                return (
                  <li
                    key={index}
                    className={tw(
                      'my-1 flex items-center leading-[1.618]',
                      index > 0 && 'hidden md:flex',
                    )}
                  >
                    <CheckIcon className="mr-1.5 text-[--checkIconColor]" />
                    <span dangerouslySetInnerHTML={{ __html: feat }} />
                  </li>
                )
              })}
            </ul>
          ) : null}
        </div>
        <div className="mt-[20.5px] hidden w-[41%] flex-col items-center md:flex">
          <Button
            asChild
            onClick={onButtonClick}
            variant="primary"
            size="large"
            className="w-full uppercase bg-secondary-700 hover:bg-[#1b6633] hidden md:flex"
          >
            <StartSubscriptionButton
              subscriptionPlan={SubscriptionPlan.CoinPackMonthly700k}
              isTrial={false}
              entryPoint={PAYMENT_ENTRY_POINTS.BANNER_ABOVE_NAV}
            >
              <span>{buttonText}</span>
            </StartSubscriptionButton>
          </Button>

          <p className="mt-[9px] text-xs">{underButtonText}</p>
        </div>
      </BannerContainer>
    </div>
  )
}
